/* eslint-disable @typescript-eslint/no-explicit-any */
import { Result, ok, err } from "neverthrow";
import { WheeError } from "../error";

// some of this is proudly copied from: https://josephlenton.com/post/adding-result-versitility-to-async-error-handling-in-typescript
/**
 * A wrapper around the Promise class that can let users define errors as part of the return values.
 * All Error classes must be instanses of PRError. I'm not sure if this is the best way, but I think doing this
 *  gives users of this class predictable errors.
 */

type PRError = WheeError;
export class PromiseResult<V, E extends PRError> extends Promise<V> {
  private readonly parseError: (error: unknown) => E;

  constructor(
    onExecute: (resolve: (value: V) => void, reject: (error?: any) => void) => void,
    parseError: (error: unknown) => E
  ) {
    super(onExecute);
    this.parseError = parseError;
  }

  static generate<V, E extends PRError>(
    call: (...args: any[]) => Promise<V>,
    parseError: (error: unknown) => E
  ) {
    return new PromiseResult<V, E>((resolve, reject) => {
      call().then(resolve).catch(reject);
    }, parseError);
  }

  async toResult(): Promise<Result<V, E>> {
    try {
      const value = await this;
      return ok(value);
    } catch (error: unknown) {
      const parsed = this.parseError(error);
      return err(parsed);
    }
  }

  andThen<U, F extends PRError>(
    f: (t: V) => PromiseResult<U, F>,
    parseError?: (error: unknown) => F
  ): PromiseResult<U, E | F> {
    return new PromiseResult(
      (resolve, reject) => {
        this.then((val) => {
          const thing = f(val).toResult();

          // No need to catch, because of the call to toResult()
          thing.then((val) => {
            if (val.isErr()) {
              reject(val.error);
              return;
            }
            resolve(val.value);
          });
        }).catch(reject);
      },
      (e) => {
        if (parseError) {
          return parseError(e);
        } else {
          return this.parseError(e);
        }
      }
    );
  }

  asyncAndThen<U, F extends PRError>(
    f: (t: V) => Promise<U>,
    parseError?: (error: unknown) => F
  ): PromiseResult<U, E | F> {
    return new PromiseResult(
      (resolve, reject) => {
        this.then(async (val) => {
          const thing = await f(val);
          resolve(thing);
        }).catch(reject);
      },
      (e) => {
        if (parseError) {
          return parseError(e);
        } else {
          return this.parseError(e);
        }
      }
    );
  }

  map<U>(f: (t: V) => U): PromiseResult<U, E> {
    return new PromiseResult(
      (resolve, reject) => {
        this.then((val) => {
          resolve(f(val));
        }).catch(reject);
      },
      (e) => {
        return this.parseError(e);
      }
    );
  }

  asyncMap<U>(f: (t: V) => U): PromiseResult<U, E> {
    return new PromiseResult(
      (resolve, reject) => {
        this.then(async (val) => {
          const thing = await f(val);
          resolve(thing);
        }).catch(reject);
      },
      (e) => {
        return this.parseError(e);
      }
    );
  }
}
